import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import {
  aggregateReportedCostPerWFromRows,
  SOLAR_COST_SERIES_START_YEAR,
  type PvCostAggregateRow,
  type SolarAggregateCostFact,
} from '../../domains/narrative/solarCostStatistics';

interface SolarPVCostData {
  chartData: any;
  chartOptions: any;
  narrative?: string;
  latestYear?: number;
  baselineYear?: number;
  percentChange?: number;
  costTrend?: 'downward' | 'mixed' | 'stable';
  coverageUnavailable?: boolean;
  nominalDollarsLabel?: string;
  latestResidentialAggregateCostPerW?: number | null;
  latestCommercialAggregateCostPerW?: number | null;
  representedApplicationCount?: number | null;
  latestResidentialCostFact?: SolarAggregateCostFact;
  aggregateCostDefinition?: string;
}

async function getGeofipsForLocation(locationName: string): Promise<number | null> {
  try {
    return await supabaseLocationService.getGeofips(locationName);
  } catch (error) {
    console.error('Error getting geofips for location:', locationName, error);
    return null;
  }
}

function emptyCostResult(location: string, reason: string): SolarPVCostData {
  return {
    coverageUnavailable: true,
    chartData: { labels: [] as number[], datasets: [] as { label: string; data: (number | null)[] }[] },
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { title: { display: true, text: `Reported Cost ($/W DC) — ${location}` } },
    },
    narrative: reason,
    nominalDollarsLabel: 'Nominal $/W DC',
    latestResidentialAggregateCostPerW: null,
    latestCommercialAggregateCostPerW: null,
    representedApplicationCount: null,
    aggregateCostDefinition:
      'Capacity-weighted aggregate reported project cost ($/W DC): SUM(reported cost basis) / SUM(DC watts) across valid aggregate rows.',
  };
}

export default async function fetchSolarPVCostSupabase(location: string): Promise<SolarPVCostData> {
  try {
    const geofips = await getGeofipsForLocation(location);

    if (!geofips) {
      return emptyCostResult(location, `No geofips found for ${location}.`);
    }

    let { data: pvData, error } = await supabaseClient
      .from('all_pv_cost')
      .select('*')
      .eq('geoid', geofips)
      .order('year', { ascending: true });

    if (error) {
      console.error('Error fetching Solar PV Cost data:', error);
      throw error;
    }

    if (!pvData || pvData.length === 0) {
      const { data: byLocation, error: locErr } = await supabaseClient
        .from('all_pv_cost')
        .select('*')
        .ilike('location_name', `%${location}%`)
        .order('year', { ascending: true });
      if (!locErr && byLocation && byLocation.length > 0) {
        pvData = byLocation;
      }
    }

    if (!pvData || pvData.length === 0) {
      return emptyCostResult(location, `No DGStats cost records for ${location}.`);
    }

    const pvRows = pvData as PvCostAggregateRow[];

    const allYears = Array.from(new Set(pvRows.map((item) => Number(item.year)))).sort(
      (a: number, b: number) => a - b
    );
    const years = allYears.filter((year) => year >= SOLAR_COST_SERIES_START_YEAR && year <= 2024);
    const sectors = ['residential', 'commercial'];

    const datasets = sectors.map((sector, index) => {
      const colors = ['#3b82f6', '#10b981'];
      const color = colors[index % colors.length];

      const data = years.map((year) => {
        const fact = aggregateReportedCostPerWFromRows(pvRows, { year, sector });
        return fact.aggregateCostPerW ?? null;
      });

      return {
        label: sector.charAt(0).toUpperCase() + sector.slice(1),
        data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1,
        fill: false,
        tension: 0.1,
        spanGaps: false,
      };
    });

    const chartData = { labels: years, datasets };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Reported Cost ($/W DC) by Sector — ${location}`,
          font: { size: 16, weight: 'bold' as const },
        },
        legend: {
          position: 'top' as const,
          labels: { usePointStyle: true, padding: 20 },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          title: { display: true, text: 'Nominal reported cost ($/W DC)' },
          beginAtZero: true,
        },
      },
    };

    const latestYear = years.length > 0 ? Math.max(...years) : undefined;
    const baselineYear = years.length > 0 ? Math.min(...years) : undefined;

    const latestResidentialFact = latestYear != null
      ? aggregateReportedCostPerWFromRows(pvRows, { year: latestYear, sector: 'residential' })
      : aggregateReportedCostPerWFromRows(pvRows, { sector: 'residential' });

    const latestCommercialFact =
      latestYear != null
        ? aggregateReportedCostPerWFromRows(pvRows, { year: latestYear, sector: 'commercial' })
        : aggregateReportedCostPerWFromRows(pvRows, { sector: 'commercial' });

    const baselineRes =
      baselineYear != null
        ? aggregateReportedCostPerWFromRows(pvRows, { year: baselineYear, sector: 'residential' })
        : latestResidentialFact;
    const baselineCom =
      baselineYear != null
        ? aggregateReportedCostPerWFromRows(pvRows, { year: baselineYear, sector: 'commercial' })
        : latestCommercialFact;

    const baselineAvg =
      baselineRes.aggregateCostPerW != null && baselineCom.aggregateCostPerW != null
        ? (baselineRes.aggregateCostPerW + baselineCom.aggregateCostPerW) / 2
        : baselineRes.aggregateCostPerW ?? baselineCom.aggregateCostPerW ?? 0;
    const latestAvg =
      latestResidentialFact.aggregateCostPerW != null && latestCommercialFact.aggregateCostPerW != null
        ? (latestResidentialFact.aggregateCostPerW + latestCommercialFact.aggregateCostPerW) / 2
        : latestResidentialFact.aggregateCostPerW ?? latestCommercialFact.aggregateCostPerW ?? 0;
    const percentChange = baselineAvg > 0 ? ((latestAvg - baselineAvg) / baselineAvg) * 100 : 0;
    const costTrend: 'downward' | 'mixed' | 'stable' =
      percentChange < -5 ? 'downward' : percentChange > 5 ? 'mixed' : 'stable';

    const aggregateCostDefinition =
      'Capacity-weighted aggregate reported project cost ($/W DC): SUM(reported cost basis) / SUM(DC watts) across valid Interconnected Project Sites aggregate rows (or capacity-weighted combination of row ratios when total_cost is absent).';

    const narrative = `${location}: ${aggregateCostDefinition} Nominal dollars; series from ${SOLAR_COST_SERIES_START_YEAR}.`;

    return {
      chartData,
      chartOptions,
      narrative,
      latestYear,
      baselineYear,
      percentChange,
      costTrend,
      latestResidentialAggregateCostPerW: latestResidentialFact.aggregateCostPerW,
      latestCommercialAggregateCostPerW: latestCommercialFact.aggregateCostPerW,
      representedApplicationCount: latestResidentialFact.representedProjectCount,
      latestResidentialCostFact: latestResidentialFact,
      nominalDollarsLabel: 'Nominal $/W DC',
      coverageUnavailable: false,
      aggregateCostDefinition,
    };
  } catch (error) {
    console.error('Error in fetchSolarPVCostSupabase:', error);
    throw error;
  }
}
